import clsx from "clsx";
import { Form, Formik } from "formik";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { ICommunicationType } from "../../../interfaces/Correspondence";
import { AppState } from "../../../redux/reducers/rootReducer";
import { useStyles } from "../../../shared/styles/useStyles";
import {
  uiCloseModalAction,
  uiCloseErrorAlert,
} from "../../../redux/actions/uiActions";
import { startReclassifySettled } from "../../../redux/actions/documentManagmentActions";
import { MyAlert } from "../../custom/MyAlert";
import { optionsCommunicationType } from "../../../shared/constants/communicationTypes";

export const ReclassifyForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const { activeCorrespondence } = useSelector(
    (state: AppState) => state.documentManagment
  );
  const { paramsProcedure } = useSelector(
    (state: AppState) => state.correspondence
  );
  const [communicationTypeSelected, setCommunicationTypeSelected] =
    useState<ICommunicationType | null>(
      activeCorrespondence ? activeCorrespondence.communicationType : null
    );
  const { errorAlert, errorMsg } = useSelector((state: AppState) => state.ui);
  const [errorCommunicationType, setErrorCommunicationType] = useState(false);

  const initialValues = {
    communicationType: activeCorrespondence?.communicationType || { isParam: false, option: "" },
    responseTimeDays: ["Guardianship", "IncidentOfContempt", "CommunicationsControlEntities"].includes(
      activeCorrespondence?.communicationType?.option || ""
    )
      ? String(activeCorrespondence?.responseTimeDays || "")
      : "",
  };

  const validationSchema = yup.object().shape({
    communicationType: yup.object().required(),
    responseTimeDays: yup
      .number()
      .when("communicationType", {
        is: (val: ICommunicationType) =>
          ["Guardianship", "IncidentOfContempt", "CommunicationsControlEntities"].includes(val?.option),
        then: yup
          .number()
          .typeError("Debe ser un número")
          .required("Campo obligatorio")
          .min(1, "Debe ser mayor a 0"),
        otherwise: yup.number().notRequired(),
      }),
  });
  const onClose = () => {
    dispatch(uiCloseModalAction());
  };

  //Vienen desde bd en la parte de parametrizacion de tiempos de respueta
  const _optionsCommunicationType: ICommunicationType[] = [
    ...optionsCommunicationType,
  ];
  paramsProcedure.map((item: any) => {
    return _optionsCommunicationType.push({
      isParam: true,
      option: item.procedureName,
      responseTime: item.responseTime,
    });
  });

  const handleSelectCommunicationType = (value: ICommunicationType | null) => {
    value ? setErrorCommunicationType(false) : setErrorCommunicationType(true);

    setCommunicationTypeSelected(value);
  };

  return (
    <Box m={2} mt={-1} mb={2}>
      <Formik
        validateOnChange={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (!errorCommunicationType) {
            communicationTypeSelected &&
              (values.communicationType = communicationTypeSelected);
            setSubmitting(true);
            await dispatch(
              startReclassifySettled(
                {
                  communicationType: values.communicationType,
                  responseTimeDays: values.responseTimeDays,
                },
                intl
              )
            );
            setSubmitting(false);
          }
        }}
      >
        {({
            isSubmitting,
            values,
            errors,
            touched,
            setFieldValue,
            handleBlur,
          }) => (
          <Form className={classes.input}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <label className="form-text">
                  <FormattedMessage id="SelectCorrectTypeCommunication" />:
                </label>

                <Autocomplete
                  id="communicationType"
                  options={_optionsCommunicationType}
                  disableCloseOnSelect
                  value={communicationTypeSelected}
                  getOptionLabel={(option) =>
                    intl.formatMessage({ id: `${option.option}` })
                  }
                  getOptionSelected={(option, value) =>
                    option.option === value.option
                  }
                  // @ts-ignore
                  onChange={(event: any, value: ICommunicationType | null) =>
                    handleSelectCommunicationType(value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      name="communicationType"
                      className={classes.myTextFieldRoot}
                      helperText={
                        errorCommunicationType &&
                        `${intl.formatMessage({ id: "RequiredFile" })}`
                      }
                      error={errorCommunicationType}
                      FormHelperTextProps={{
                        className: classes.helperText,
                      }}
                    />
                  )}
                />
              </Grid>
              {["Guardianship", "IncidentOfContempt", "CommunicationsControlEntities"].includes(communicationTypeSelected?.option || "") && (
                <Grid item xs={4}>
                  <label className="form-text">
                    <FormattedMessage id="Days" />:
                  </label>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    name="responseTimeDays"
                    type="number"
                    value={values.responseTimeDays}
                    onChange={(e) => setFieldValue("responseTimeDays", e.target.value)}
                    onBlur={handleBlur}
                    className={classes.myTextFieldRoot}
                    helperText={
                      touched.responseTimeDays && errors.responseTimeDays
                        ? String(errors.responseTimeDays)
                        : ""
                    }
                    error={Boolean(touched.responseTimeDays && errors.responseTimeDays)}
                    FormHelperTextProps={{
                      className: classes.helperText,
                    }}
                  />
                </Grid>
              )}


              <Box mt={2} display="flex" flexDirection="row-reverse" width={1}>
                {!isSubmitting ? (
                  <Button
                    className={clsx(classes.btn, classes.save)}
                    autoFocus
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <FormattedMessage id="Save" />
                  </Button>
                ) : (
                  <Button
                    className={clsx(classes.btn, classes.save)}
                    autoFocus
                    type="button"
                    disabled={true}
                  >
                    <CircularProgress className={classes.btnLoading} />
                  </Button>
                )}
                <Button
                  className={clsx(classes.btn, classes.cancel)}
                  onClick={onClose}
                >
                  <FormattedMessage id="Cancel" />
                </Button>
              </Box>
            </Grid>
          </Form>
        )}
      </Formik>

      {errorMsg && (
        <MyAlert
          open={errorAlert}
          typeAlert="error"
          message={errorMsg}
          time={2000}
          handleClose={() => dispatch(uiCloseErrorAlert())}
        />
      )}
    </Box>
  );
};
